import axios from 'axios';
import i18next from 'i18next';
import { markSessionExpired } from './sessionExpiredNotice';

/**
 * Global Axios interceptor for handling network errors.
 * Prevents unhandled promise rejections (Sentry onunhandledrejection)
 * by catching transient network failures and retrying once.
 *
 * NOTE: Only safe (idempotent) HTTP methods are retried (GET, HEAD, OPTIONS).
 * NOTE: This applies to the global axios instance only. axios.create() instances
 *       (e.g., RayKeyClient) are not affected.
 *
 * Related: BUG-013 — Axios Network Error (CORS/API unreachable)
 * Related: BUG-061 — AxiosError timeout exceeded (global default timeout)
 * IMP-045: Layer 2 보강 — 401 세션 만료 글로벌 처리 + 5xx 서버 오류 글로벌 토스트
 * IMP-189: 404 deploy-skew 전역 처리 — API Gateway 미등록 라우트(BE 미배포) 의심 시 토스트 안내
 * IMP-410: 권한 drop 응답 계약(meta.dropped) 전역 안내 — 200 OK 성공 분기에서 BE 가 권한 사유로
 *          제거한 필드를 사용자에게 알린다(silent-drop 금지, CLAUDE.md 규칙 7).
 *          응답 1건 = 토스트 1건(모든 reason 을 한 문구로 결합)이며, 뒤따르는 성공 토스트에
 *          덮이지 않도록 매크로태스크로 지연 dispatch 한다. console.warn 은 throttle 과 무관하게
 *          항상 남긴다.
 *          한계: 1초 throttle 창 안의 후속 응답은 토스트가 생략된다(console.warn 만 남음) —
 *          대량 가져오기(AdminProductImport) 같은 순차 다건 호출의 결과 표시는 본 전역 안내의
 *          범위가 아니며 BUG-195(#343) 소관이다.
 *
 * [IMP-045 커버리지 경계]
 * - 본 글로벌 401/5xx 처리는 글로벌 axios 인스턴스에만 적용된다.
 * - axios.create() 인스턴스(예: src/lib/rayKeyClient.js)는 미적용 — 단 rayKeyClient 는
 *   외부(rayteams key) 도메인 + 자체 retry 인터셉터를 보유하여 RAYGuard 세션과 무관(의도적 제외).
 * - src/lib/rayteamsLocalClient.js(localhost:8008) 등 외부 서비스도
 *   URL opt-in(isRayguardApiRequest)으로 제외된다.
 */

// IMP-045: 글로벌 401/5xx 처리는 RAYGuard 자체 API(API_SERVER_PATH) 요청에만 적용.
// 외부 서비스(rayteams 게이트웨이/iubenda/rayplatform 등)의 401/5xx 는 RAYGuard 세션과 무관하므로 제외.
// 경계 매칭: startsWith(base + '/') || url === base — lookalike({base}.evil.com) 차단
const isRayguardApiRequest = (config) => {
    if (!config) return false;
    const AppConfig = require('../config').AppConfig || require('../config').default;
    const base = (AppConfig && AppConfig.API_SERVER_PATH) || '';
    if (!base) return false;
    const url = (config.baseURL || '') + (config.url || '');
    return url === base || url.startsWith(base + '/');
};

// IMP-045: 5xx 토스트 throttle — 1초 이내 연속 발생 시 두 번째 이후 생략
// (테스트 환경: throttle_ms = 0 → "동일 ms 이내 중복만 생략", 테스트 격리 지원)
let lastServerErrorToastAt = 0;
const SERVER_ERROR_TOAST_THROTTLE_MS = process.env.NODE_ENV === 'test' ? 0 : 1000;

// IMP-189: 404 deploy-skew 토스트 throttle — 5xx throttle 과 독립된 변수
let lastNotFoundToastAt = 0;
const NOT_FOUND_TOAST_THROTTLE_MS = process.env.NODE_ENV === 'test' ? 0 : 1000;

// IMP-410: meta.dropped 안내 토스트 throttle — 5xx/404 throttle 과 독립된 변수
let lastDropNoticeToastAt = 0;
const DROP_NOTICE_TOAST_THROTTLE_MS = process.env.NODE_ENV === 'test' ? 0 : 1000;

// IMP-410: reason enum → 공통 안내 문구 키 (Spec FR-007 §3.2.1). 필드별 개별 번역 없음.
// 키 자체는 i18n 담당이 19개 언어에 추가한다. 매핑 실패(미지의 reason)는 기본 문구로 폴백.
const DROP_REASON_MESSAGE_KEYS = {
    OUT_OF_SCOPE: 'permissionDropOutOfScope',
    INSUFFICIENT_ROLE: 'permissionDropInsufficientRole',
    IMMUTABLE_FIELD: 'permissionDropImmutableField',
    INVALID_VALUE: 'permissionDropInvalidValue',
};
const DROP_REASON_FALLBACK_KEY = 'permissionDropNotice';

// IMP-410: 안내 문구 나열 상한 — (1) 표시할 reason 그룹 수, (2) 그룹당 나열할 필드 수.
// 근거: 한 응답의 안내는 토스트 1건으로 합쳐 dispatch 한다(아래 지연 dispatch 주석). AlertReducer 의
// message 는 단일 슬롯이고 App.js 는 그 문자열을 Toast 한 줄로 렌더(CommonViews.js Toast)하므로
// 문구가 길어지면 잘려서 읽히지 않는다. reason enum 은 4종, 한 응답의 dropped 필드 수는 상한이
// 없으므로 두 축을 모두 잘라 문구 길이를 결정적으로 묶는다(최대 = 안내문 2개 + 필드명 6개).
// 생략분은 `(+N)` 으로 존재를 알리고 console.warn 에 전량 기록한다
// (silent-drop 금지 — 누락이 아니라 표시 상한. CLAUDE.md 규칙 7).
const MAX_DROP_NOTICE_REASONS = 2;
const MAX_DROP_NOTICE_FIELDS = 3;

// IMP-410: 안내 dispatch 를 매크로태스크(setTimeout 0)로 미뤄 "마지막 알림" 이 되도록 보장한다.
// 문제: AlertReducer 는 message 단일 슬롯이라 나중 dispatch 가 앞선 것을 덮고, App.js 는
//   useEffect([alert]) 로 Toast 1개만 렌더한다. 응답 인터셉터는 호출부의 `await axios...` 재개
//   (마이크로태스크)보다 먼저 실행되므로 여기서 즉시 dispatch 하면 곧이어 실행되는
//   dispatch(AlertAction.Success("Updated!")) 가 안내를 덮어쓴다(= IMP-410 이 없애려던 무음 drop).
// 해결: setTimeout 0(매크로태스크)은 그 마이크로태스크 체인이 모두 끝난 뒤 실행되므로,
//   응답 직후 동기적으로 성공 토스트를 띄우는 경로에서는 안내가 항상 마지막에 남는다.
// 기존 401/5xx/404 와 어긋나지 않는 이유: 그 셋은 실패(reject) 분기여서 뒤따르는 호출부 처리가
//   에러 토스트이거나 무동작이라 "성공 토스트가 덮는" 충돌이 없고, 특히 401 은 즉시 로그아웃/
//   리다이렉트해야 하므로 지연시키면 안 된다. 지연은 성공(200) 분기에만 적용한다.
// 알려진 한계: 호출부가 응답 뒤 추가 await(목록 재조회 등) 후에 성공 토스트를 띄우면 여전히
//   덮인다. 그 경우까지 없애려면 알림을 큐로 바꿔야 하는데(AlertReducer/App.js) 영향 범위가
//   커서 본 변경에서는 다루지 않는다.
export const DROP_NOTICE_DISPATCH_DELAY_MS = 0;

/**
 * IMP-410: meta.dropped 배열 → reason 별 안내 그룹으로 묶는다(최초 등장 순서 보존).
 * 방어: 배열이 아니거나 항목이 null/비객체/비문자열 필드여도 throw 하지 않는다.
 * @param {*} dropped 응답 meta.dropped (신뢰할 수 없는 외부 입력)
 * @returns {Array<{ reason: string, key: string, fields: string[] }>}
 */
export const groupDroppedByReason = (dropped) => {
    if (!Array.isArray(dropped) || dropped.length === 0) return [];
    const groups = [];
    const indexByReason = Object.create(null); // prototype 키(__proto__ 등) 오염 방지
    dropped.forEach((item) => {
        if (!item || typeof item !== 'object' || Array.isArray(item)) return;
        const rawReason = typeof item.reason === 'string' ? item.reason.trim() : '';
        const isKnownReason = !!rawReason
            && Object.prototype.hasOwnProperty.call(DROP_REASON_MESSAGE_KEYS, rawReason);
        const reason = isKnownReason ? rawReason : 'UNKNOWN';
        const key = isKnownReason ? DROP_REASON_MESSAGE_KEYS[rawReason] : DROP_REASON_FALLBACK_KEY;
        let group = indexByReason[reason];
        if (!group) {
            group = { reason, key, fields: [] };
            indexByReason[reason] = group;
            groups.push(group);
        }
        // field 가 비문자열/빈 문자열이면 필드명만 생략하고 reason 안내는 유지한다.
        const field = typeof item.field === 'string' ? item.field.trim() : '';
        if (field && !group.fields.includes(field)) group.fields.push(field);
    });
    return groups;
};

/**
 * IMP-410: reason 그룹 → 토스트 1건용 단일 문구. (응답 1건 = dispatch 1회)
 * 저장소가 i18n interpolation 을 지원하지 않아 문자열 결합으로 조립한다.
 * 생략 건수는 새 i18n 키 없이 알리기 위해 언어 중립 표기 `(+N)` 을 쓴다(lang.*.json 은 별도 담당).
 * @param {Array<{reason:string,key:string,fields:string[]}>} groups groupDroppedByReason 결과
 * @param {(key:string)=>string} translate i18next.t 호환 함수
 * @returns {string} 예) "담당 조직 범위를 벗어난 항목은 저장되지 않았습니다: companyId, siteId"
 */
export const buildDropNoticeMessage = (groups, translate) => {
    const shownGroups = Array.isArray(groups) ? groups.slice(0, MAX_DROP_NOTICE_REASONS) : [];
    const parts = shownGroups.map((g) => {
        let base = g.key;
        try {
            const translated = typeof translate === 'function' ? translate(g.key) : '';
            if (typeof translated === 'string' && translated) base = translated;
        } catch (e) {
            // 번역 실패는 키 노출로 폴백 — 안내 자체는 유지(무음 금지).
            console.warn('[Axios dropNotice:error] 문구 번역 실패 — 키로 폴백', { key: g.key, error: e && e.message });
        }
        const fields = Array.isArray(g.fields) ? g.fields : [];
        const shownFields = fields.slice(0, MAX_DROP_NOTICE_FIELDS);
        if (shownFields.length === 0) return base;
        const hiddenFields = fields.length - shownFields.length;
        const list = hiddenFields > 0
            ? `${shownFields.join(', ')} (+${hiddenFields})`
            : shownFields.join(', ');
        return `${base}: ${list}`;
    });
    const hiddenGroups = (Array.isArray(groups) ? groups.length : 0) - shownGroups.length;
    const message = parts.join(' / ');
    return hiddenGroups > 0 ? `${message} (+${hiddenGroups})` : message;
};

/**
 * IMP-410: 성공(200) 응답의 meta.dropped 감지 → console.warn + 안내 토스트.
 * 원본 response 는 호출부에서 그대로 반환하며 본 함수는 반환값을 쓰지 않는다.
 * store/AlertAction 는 순환 참조 회피를 위해 함수 안에서 require (5xx/404 와 동일).
 * i18next 는 예외 — 함수 내부 require 는 prod 번들(CRA4/webpack4, i18next dual entry)에서
 * ESM 네임스페이스 객체를 반환해 `.t` 가 undefined 가 된다(BUG-247). 파일 최상단
 * `import i18next from 'i18next'` 를 그대로 써야 babel interop 으로 정상 동작한다 — 되돌리지 말 것.
 */
const noticeDroppedFields = (response) => {
    const config = response && response.config;
    // RAYGuard 자체 API 요청에만 적용(외부 rayteams 게이트웨이 등 제외) + opt-out 지원
    if (!isRayguardApiRequest(config)) return;
    if (config && config.skipGlobalDropNotice) return;

    const data = response && response.data;
    if (!data || typeof data !== 'object' || Array.isArray(data)) return; // 문자열/Blob 바디 방어
    const meta = data.meta;
    if (!meta || typeof meta !== 'object' || Array.isArray(meta)) return; // meta 가 배열이어도 무시
    const groups = groupDroppedByReason(meta.dropped);
    if (groups.length === 0) return;

    // M-3: throttle 은 토스트(dispatch)에만 적용한다. console.warn 은 throttle 앞에 두어
    // drop 이 발생한 모든 응답에 대해 항상 남긴다 — 대량 가져오기처럼 1초 내 연속 응답이 오는
    // 경로(AdminProductImport 의 청크 호출)에서 토스트도 로그도 없는 완전 무음이 되면
    // CLAUDE.md 규칙 7(가드는 관측 가능한 경고 동반)을 위반하고 본 기능의 목적 자체가 무너진다.
    const now = Date.now();
    if (now < lastDropNoticeToastAt) { lastDropNoticeToastAt = 0; } // clock 역방향 가드
    const throttled = (now - lastDropNoticeToastAt) <= DROP_NOTICE_TOAST_THROTTLE_MS;

    const handlers = Array.isArray(meta.dropped)
        ? meta.dropped
            .map((it) => (it && typeof it.handler === 'string' ? it.handler : null))
            .filter((h, i, arr) => !!h && arr.indexOf(h) === i)
        : [];

    console.warn('[Axios meta.dropped] 권한 사유로 일부 필드가 저장되지 않음', {
        url: config && config.url,
        method: config && config.method,
        handlers,
        dropped: groups.map((g) => ({ reason: g.reason, fields: g.fields })),
        shown: throttled ? 0 : Math.min(groups.length, MAX_DROP_NOTICE_REASONS),
        total: groups.length,
        // 토스트가 생략됐는지 로그만으로 판별 가능하게 남긴다(무음 여부 관측).
        toastThrottled: throttled,
    });

    if (throttled) return;
    lastDropNoticeToastAt = now;

    // 한 응답의 모든 reason 을 단일 문구로 합쳐 dispatch 1회 — message 슬롯이 1개라 2회 dispatch 는
    //   '2건 노출' 이 아니라 '앞엣것을 덮어쓰기' 였다.
    // 지연 실행이라 상위 try/catch 밖이다 — 이 콜백 본문에서 난 예외만 아래 catch 로 격리한다
    //   (store.dispatch 가 돌려주는 거부된 Promise 까지는 덮지 못한다).
    setTimeout(() => {
        try {
            const { store } = require('../store');
            const { AlertAction } = require('../actions/AlertAction');
            const message = buildDropNoticeMessage(groups, (k) => i18next.t(k));
            if (!message) return;
            store.dispatch(AlertAction.ErrorMsg(message));
        } catch (e) {
            // m-6: 정상 안내 warn(`[Axios meta.dropped]`)과 prefix 를 구분해
            // 로그에서 "안내함" 과 "안내 실패" 를 분리 가능하게 한다.
            console.warn('[Axios dropNotice:error] 안내 토스트 표시 실패', e && e.message);
        }
    }, DROP_NOTICE_DISPATCH_DELAY_MS);
};

// Global default timeout (30 seconds) to prevent requests from hanging indefinitely
axios.defaults.timeout = 30000;

const MAX_RETRY = 1;
const RETRY_DELAY = 1500;
const SAFE_METHODS = ['get', 'head', 'options'];

axios.interceptors.response.use(
    (response) => {
        // IMP-410: 인터셉터에서 예외가 나면 정상 응답이 통째로 실패하므로 안내 처리를 격리한다.
        try {
            noticeDroppedFields(response);
        } catch (e) {
            // m-6: 정상 안내 warn(`[Axios meta.dropped]`)과 구분되는 prefix.
            console.warn('[Axios dropNotice:error] 안내 처리 실패 — 응답은 그대로 전달', e && e.message);
        }
        return response;
    },
    async (error) => {
        const config = error.config;
        if (!config) return Promise.reject(error);

        const isNetworkError = !error.response && error.message === 'Network Error';
        const isTimeout = error.code === 'ECONNABORTED';
        const isSafeMethod = config.method && SAFE_METHODS.includes(config.method.toLowerCase());

        config.__retryCount = config.__retryCount || 0;

        if ((isNetworkError || isTimeout) && isSafeMethod && config.__retryCount < MAX_RETRY) {
            config.__retryCount += 1;
            await new Promise((resolve) => setTimeout(resolve, RETRY_DELAY));
            return axios(config);
        }

        if (isNetworkError || isTimeout) {
            console.warn(
                '[Axios] Network Error — request failed without response.',
                'URL:', config.url,
                'Method:', config.method
            );
        }

        const status = error.response && error.response.status;

        // IMP-045 Layer 2: 401 — 세션 만료 글로벌 처리 (RAYGuard 자체 API 요청에만 적용)
        if (status === 401 && isRayguardApiRequest(config) && !(config && config.skipGlobalAuthHandler)) {
            const { store } = require('../store');
            const { AuthAction } = require('../actions/AuthAction');
            const { history } = require('../history');
            const { resolveUnauthRedirect } = require('./loginRedirect');
            console.warn('[Axios 401] 세션 만료 — 글로벌 로그아웃/리다이렉트', { url: config && config.url });
            // U-44: 로그인 화면으로 보내질 때만(공개·로그인 경로 제외) 만료 안내 표식을 남긴다. 로그아웃·이동 조건은 그대로.
            if (resolveUnauthRedirect(window.location.pathname)) markSessionExpired();
            store.dispatch(AuthAction.Logout());
            const target = resolveUnauthRedirect(window.location.pathname);
            if (target) history.replace(target);
        }

        // IMP-045 Layer 2: 5xx — 서버 오류 글로벌 토스트 (throttle 1초, RAYGuard 자체 API 요청에만 적용)
        if (status >= 500 && isRayguardApiRequest(config) && !(config && config.skipGlobalErrorToast)) {
            const { store } = require('../store');
            const { AlertAction } = require('../actions/AlertAction');
            const now = Date.now();
            // clock 역방향 감지 (fake timer로 과거 시각 고정 시) → throttle state 자동 리셋
            if (now < lastServerErrorToastAt) {
                lastServerErrorToastAt = 0;
            }
            if (now - lastServerErrorToastAt > SERVER_ERROR_TOAST_THROTTLE_MS) {
                lastServerErrorToastAt = now;
                console.warn('[Axios 5xx] 서버 오류 — 글로벌 토스트', { status, url: config && config.url });
                store.dispatch(AlertAction.ErrorMsg(i18next.t('serverErrorOccurred')));
            }
        }

        // IMP-189: 404 — 미등록 라우트(BE 미배포/deploy-skew) 의심 시 전역 안내 토스트.
        //   정상 앱 404(RError → { status:"fail", errmsg:<code> })는 기존 핸들러에 위임(글로벌 동작 없음) → 오탐 차단.
        //   API Gateway 미등록 라우트 404는 앱 필드가 없는 { message:"Not Found" } 형태 → 이것만 deploy-skew 로 간주.
        if (status === 404 && isRayguardApiRequest(config) && !(config && config.skipGlobalNotFoundHandler)) {
            const data = error.response && error.response.data;
            const isAppHandledNotFound =
                data && typeof data === 'object' && !Array.isArray(data)
                && (Object.prototype.hasOwnProperty.call(data, 'errmsg') || data.status === 'fail');
            if (!isAppHandledNotFound) {
                const now = Date.now();
                if (now < lastNotFoundToastAt) { lastNotFoundToastAt = 0; } // clock 역방향 가드
                if (now - lastNotFoundToastAt > NOT_FOUND_TOAST_THROTTLE_MS) {
                    lastNotFoundToastAt = now;
                    const { store } = require('../store');
                    const { AlertAction } = require('../actions/AlertAction');
                    console.warn('[Axios 404] 미등록 라우트(deploy-skew) 의심 — BE 미배포 가능성', {
                        status,
                        url: config && config.url,
                        method: config && config.method,
                        reason: 'API Gateway route not found (no app errmsg in body)',
                    });
                    store.dispatch(AlertAction.ErrorMsg(i18next.t('featureTemporarilyUnavailable')));
                }
            }
        }

        return Promise.reject(error);
    }
);
